import { useMemo } from 'react'

import { AccessType } from 'utils/constants'
import {
  LeadOwner,
  UserInfo,
  isLeadAccessible,
  isLeadCustodian,
  isMyTeamMembersIsCollaboratorInLead,
} from 'utils/generic'
import useWorkFlowLookup from './lookup/use-workflow-lookup'
import useUser from './use-user'
import useUserPermissions from './use-user-permissions'

interface Props {
  leadOwner: LeadOwner
  collaborators: string[] | null
  isQueuedForUser: boolean
}

export const useLeadAccessible = ({ leadOwner, collaborators, isQueuedForUser = false }: Props) => {
  const { currentUser } = useUser()
  const { teamOptions } = useWorkFlowLookup()
  const [accountWorkflowPermissions] = useUserPermissions('accountWorkflow')

  const userInfo: UserInfo = useMemo(
    () => ({
      id: currentUser?.id,
      teamIds: currentUser?.teams,
    }),
    [currentUser],
  )

  const userAllTeamMembers: string[] = useMemo(() => {
    if (!currentUser?.teams) return []

    const usersTeamMembers = new Set(
      teamOptions?.filter((t) => currentUser?.teams.includes(t.value)).flatMap((item: any) => item?.teamMembers ?? []),
    )

    return Array.from(usersTeamMembers)
  }, [currentUser?.teams, teamOptions])

  const isCustodian: boolean = useMemo(
    () =>
      isLeadCustodian(
        userInfo,
        leadOwner,
        collaborators,
        isQueuedForUser,
        userAllTeamMembers,
        accountWorkflowPermissions,
      ),
    [userInfo, leadOwner, collaborators, isQueuedForUser, accountWorkflowPermissions, userAllTeamMembers],
  )

  const isTeamMembersInCollaborator: boolean | undefined = useMemo(
    () =>
      isMyTeamMembersIsCollaboratorInLead({
        permissions: accountWorkflowPermissions,
        currentUsersTeamMembers: userAllTeamMembers,
        collaborators,
      }),
    [accountWorkflowPermissions, collaborators, userAllTeamMembers],
  )

  const { canEdit, canView }: { canEdit: boolean; canView: boolean } = useMemo(
    () => ({
      canEdit: isLeadAccessible(
        userInfo,
        leadOwner,
        collaborators,
        accountWorkflowPermissions,
        userAllTeamMembers,
        AccessType.edit,
        isQueuedForUser,
      ),
      canView: isLeadAccessible(
        userInfo,
        leadOwner,
        collaborators,
        accountWorkflowPermissions,
        userAllTeamMembers,
        AccessType.view,
        isQueuedForUser,
      ),
    }),
    [accountWorkflowPermissions, collaborators, isQueuedForUser, leadOwner, userAllTeamMembers, userInfo],
  )

  return {
    isCustodian,
    isTeamMembersInCollaborator,
    canEdit,
    canView,
  }
}
